import { Dir } from '@angular/cdk/bidi';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, inject, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/list';
import { PushPipe } from '@ngrx/component';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { TopicFullScreenDialogComponent } from '@src/suite/common/st-full-screen-dialog/topic-full-screen/topic-full-screen';
import { selectCurrentRecord, selectNewRecordCreationInProgress } from '@src/suite/common/store/fullscreen';
import { selectTopicPropertiesSortedBySequence } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';

import { LoaderComponent } from '../../../../loader/loader';
import { AddressFormComponent } from '../../../controls/address-form/address-form';
import { CheckBoxComponent } from '../../../controls/check-box/check-box';
import { CheckListComponent } from '../../../controls/check-list/check-list';
import { ComboBoxComponent } from '../../../controls/combo-box/combo-box';
import { DatePickerComponent } from '../../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../../controls/date-time-picker/date-time-picker';
import { DateTimeRangeComponent } from '../../../controls/date-time-range/date-time-range';
import { DeskComponent } from '../../../controls/desk/desk';
import { SpecialCostComponent } from '../../../controls/special/special-cost/special-cost';
import { StTextAreaComponent } from '../../../controls/st-text-area/st-text-area';
import { StTextAreaOverlayComponent } from '../../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListComponent } from '../../../controls/staff-id-list/staff-id-list';
import { StaffIDListMultipleComponent } from '../../../controls/staff-id-list/staff-id-list-multiple';
import { StatusComponent } from '../../../controls/status/status';
import { TagsAutocompleteComponent } from '../../../controls/tags/tags-autocomplete';
import { TextFieldComponent } from '../../../controls/text-field/text-field';
import { TopicTypeComponent } from '../../../controls/topic-type/topic-type';
import { TreeComponent } from '../../../controls/tree/tree';

@Component({
    selector: 'st-create-topic-full-screen',
    imports: [
        AddressFormComponent,
        CdkOverlayOrigin,
        CheckBoxComponent,
        CheckListComponent,
        ComboBoxComponent,
        DatePickerComponent,
        DateTimePickerComponent,
        DateTimeRangeComponent,
        DeskComponent,
        Dir,
        LoaderComponent,
        MatButton,
        MatDivider,
        MatRipple,
        PushPipe,
        SpecialCostComponent,
        StaffIDListComponent,
        StaffIDListMultipleComponent,
        StatusComponent,
        StTextAreaComponent,
        StTextAreaOverlayComponent,
        TagsAutocompleteComponent,
        TextFieldComponent,
        TopicTypeComponent,
        TreeComponent,
    ],
    templateUrl: './create-topic-full-screen.html',
    styleUrl: './create-topic-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-create-topic-full-screen',
    },
})
export class CreateTopicFullScreenDialogComponent extends TopicFullScreenDialogComponent {
    // computed
    readonly currentTopic = computed<StorytellingTopicRecord>(() => this.store.selectSignal(selectCurrentRecord)());

    override readonly disabled = computed(() => {
        return !this.isRequiredFieldsPopulated() || this.loading();
    });

    override readonly loading = computed(() => {
        return this.store.selectSignal(selectNewRecordCreationInProgress)();
    });

    readonly properties = computed(() => {
        if (!this.enforceRequiredFieldsOnCreate()) {
            return [];
        }
        const props = this.store.selectSignal(selectTopicPropertiesSortedBySequence)();
        return props?.filter(
            p =>
                p?.ID &&
                !this.fieldsToSkip.has(p.ID) &&
                !this.topFields.has(p.ID) &&
                this.globalTablesService.FieldDefsDict?.[p.ID]?.Required &&
                (this.data()?.visibilityCriteria?.[p.ID] || this.data()?.visibilityCriteria?.[p.ID] == null)
        );
    });

    readonly itemListMap = computed(() => {
        const itemLists = new Map<string, { id: string; title: string; selectable: boolean }[]>();
        this.properties().forEach(prop => {
            itemLists.set(prop.ID, this.globalTablesService.getListItems(prop.ID, prop.List));
        });
        return itemLists;
    });

    // fields
    fieldsToSkip = new Set<string>([PSTFieldNames.PSTTeamLocation]);
    topFields = new Set<string>([
        PSTFieldNames.Title,
        PSTFieldNames.Description,
        PSTFieldNames.PSTTopicStatus,
        PSTFieldNames.TargetPublicationDate,
        PSTFieldNames.PSTDesk,
        PSTFieldNames.PSTTopicStoryOwner,
        PSTFieldNames.PSTTopicType,
    ]);

    constructor() {
        const data = inject<{
            topic: StorytellingTopicRecord;
        }>(MAT_DIALOG_DATA);

        super();
        this.data.set(data.topic);

        effect(() => {
            if (this.currentTopic()?.Type === ObjectTypes.TOPIC) {
                this.data.set(this.currentTopic());
            }
        });
    }

    // methods
    override create(createAndNext: boolean) {
        if (!this.isRequiredFieldsPopulated()) return;
        this.store.dispatch(PlaybookApiActions.prepareCreateTopic({ topic: this.data(), createAndNext }));
    }
}
